import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MdCloudUpload, MdPlayArrow, MdPause, MdEdit, MdDelete, MdArrowUpward, MdArrowDownward } from 'react-icons/md';
import PODCAST_GENRES from '../../utils/PodcastGender';
import '../../Styles/ArtistRoot.css';
import '../../Styles/Admin.css';
import izenuAPI from '../../services/izenuAPI';

// Profile photos are resized server-side up to 1200 px.
const PHOTO_MAX_MB = 10;
const CDN = process.env.REACT_APP_CDN_URL || 'https://dpagz47n4g1lw.cloudfront.net';

const formatDuration = (ms) => {
    if (!ms) return '';
    const total = Math.round(Number(ms) / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const sec = String(total % 60).padStart(2, '0');
    return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
};
const genreLabel = (v) => (PODCAST_GENRES.find(g => g.value === v) || {}).label || v || '';

export default function Podcaster() {
    const { artist_id } = useParams();
    const [details, setDetails] = useState(null);
    const [episodes, setEpisodes] = useState([]);
    const [loadError, setLoadError] = useState(false);

    // One player for the page: starting an episode stops the previous one.
    const [playingId, setPlayingId] = useState(null);
    const audioRef = useRef(null);
    useEffect(() => {
        const audio = new Audio();
        audio.onended = () => setPlayingId(null);
        audio.onerror = () => setPlayingId(null);
        audioRef.current = audio;
        return () => { audio.pause(); audio.src = ''; };
    }, []);

    const [photoModal, setPhotoModal] = useState(false);
    const [photo, setPhoto] = useState(null);       // { file, url }
    const [photoError, setPhotoError] = useState('');
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    useEffect(() => () => { if (photo?.url) URL.revokeObjectURL(photo.url); }, [photo]);

    // Episode edit / delete
    const [editing, setEditing] = useState(null);   // { podcast_id, title, genre }
    const [savingEdit, setSavingEdit] = useState(false);
    const [editError, setEditError] = useState('');

    const saveEdit = async (e) => {
        e.preventDefault();
        if (!editing.title.trim()) { setEditError('O título não pode ficar vazio.'); return; }
        setSavingEdit(true);
        setEditError('');
        try {
            const { data: r } = await izenuAPI.post('/distributor/podcasts/update.php', {
                podcast_id: editing.podcast_id, title: editing.title.trim(), genre: editing.genre,
            });
            if (r.response) {
                setEpisodes(list => list.map(ep => ep.podcast_id === editing.podcast_id ? { ...ep, title: editing.title.trim(), genre: editing.genre } : ep));
                setEditing(null);
            } else {
                setEditError(r.message || 'Não foi possível guardar.');
            }
        } catch (err) {
            setEditError(err?.response?.data?.message || 'Não foi possível guardar.');
        }
        setSavingEdit(false);
    };

    // Optimistic: the list moves at once and goes back if the server refuses.
    const moveEpisode = async (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= episodes.length) return;
        const before = episodes;
        const next = [...episodes];
        [next[index], next[target]] = [next[target], next[index]];
        setEpisodes(next);
        try {
            const { data: r } = await izenuAPI.post('/distributor/podcasts/reorder.php', {
                podcaster_id: Number(details.user_id || artist_id),
                podcast_ids: next.map(ep => ep.podcast_id),
            });
            if (!r.response) throw new Error(r.message);
        } catch (err) {
            setEpisodes(before);
            alert(err?.response?.data?.message || err.message || 'Não foi possível guardar a ordem.');
        }
    };

    const deleteEpisode = async (ep) => {
        if (!window.confirm(`Apagar o episódio "${ep.title}"?\n\nDeixa de estar disponível na app e no site. Esta acção não pode ser desfeita.`)) return;
        try {
            const { data: r } = await izenuAPI.post('/distributor/podcasts/delete.php', { podcast_id: ep.podcast_id });
            if (r.response) {
                if (playingId === ep.podcast_id) { audioRef.current?.pause(); setPlayingId(null); }
                setEpisodes(list => list.filter(x => x.podcast_id !== ep.podcast_id));
            } else {
                alert(r.message || 'Não foi possível apagar o episódio.');
            }
        } catch (err) {
            alert(err?.response?.data?.message || 'Não foi possível apagar o episódio.');
        }
    };

    const fetchDetails = useCallback(async () => {
        try {
            const distributorId = localStorage.getItem('id');
            const { data: r } = await izenuAPI.get(`/distributor/artist/fetchPodcaster.php?limit=30&artist_id=${artist_id}&distributor_id=${distributorId}`);
            if (r.response) {
                setDetails(r.artist_details || {});
                setEpisodes(r.podcasts || []);
            } else {
                setLoadError(true);
            }
        } catch (err) {
            console.log(err);
            setLoadError(true);
        }
    }, [artist_id]);

    useEffect(() => { fetchDetails(); }, [fetchDetails]);

    const togglePlay = (ep) => {
        const audio = audioRef.current;
        if (!audio || !ep.audio) return;
        if (playingId === ep.podcast_id) {
            audio.pause();
            setPlayingId(null);
            return;
        }
        audio.src = ep.audio;
        audio.play().then(() => setPlayingId(ep.podcast_id)).catch(() => {
            setPlayingId(null);
            alert('Não foi possível reproduzir este episódio.');
        });
    };

    const pickPhoto = (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        setPhotoError('');
        if (!file) return;
        // Case-insensitive: phones name files IMG_1234.PNG / .JPG.
        if (!['jpg', 'jpeg', 'png'].includes((file.name.split('.').pop() || '').toLowerCase())) {
            setPhotoError('Formato não suportado. Usa uma imagem JPG ou PNG.');
            return;
        }
        if (file.size > PHOTO_MAX_MB * 1024 * 1024) {
            setPhotoError(`A imagem tem ${(file.size / 1024 / 1024).toFixed(1)} MB. O máximo é ${PHOTO_MAX_MB} MB.`);
            return;
        }
        setPhoto({ file, url: URL.createObjectURL(file) });
    };

    const uploadPhoto = async (e) => {
        e.preventDefault();
        if (!photo) { setPhotoError('Escolhe uma imagem primeiro.'); return; }
        setUploadingPhoto(true);
        const fd = new FormData();
        fd.append('file', photo.file);
        fd.append('user_id', details.user_id);
        try {
            // No manual Content-Type: axios must set the multipart boundary itself.
            const { data: r } = await izenuAPI.post('/distributor/artist/updateProfilePhoto.php', fd);
            if (r.response) {
                setPhotoModal(false);
                setPhoto(null);
                fetchDetails();
            } else {
                setPhotoError(r.message || 'Não foi possível actualizar a foto.');
            }
        } catch (err) {
            console.log(err);
            setPhotoError('Erro ao enviar a imagem. Tenta novamente.');
        }
        setUploadingPhoto(false);
    };

    if (loadError) {
        return (
            <div className="mx-auto max-w-5xl p-6 text-left">
                <Link to="/podcasters" className="text-sm text-amber-700 hover:underline">← Podcasters</Link>
                <p className="mt-6 text-gray-600">Não foi possível carregar este podcaster. Pode não pertencer à tua distribuidora, ou a ligação falhou.</p>
                <button type="button" onClick={() => { setLoadError(false); fetchDetails(); }}
                    className="mt-3 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium !text-white hover:bg-amber-600">Tentar novamente</button>
            </div>
        );
    }
    if (!details) return <p className="p-10 text-center text-sm text-gray-500">A carregar…</p>;

    const name = details.artist_name || details.name;
    const outlineBtn = 'rounded-lg !border !border-solid border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50';

    return (
        <div className="mx-auto max-w-5xl space-y-6 p-3 sm:p-6 text-left">
            <Link to="/podcasters" className="inline-block text-sm text-amber-700 hover:underline">← Podcasters</Link>

            {/* Profile */}
            <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
                <div className="flex flex-col gap-5 sm:flex-row">
                    <div className="relative mx-auto h-32 w-32 shrink-0 overflow-hidden rounded-xl bg-gray-200 sm:mx-0 sm:h-40 sm:w-40">
                        {details.photo ? (
                            <img className="h-full w-full object-cover" src={`${CDN}/users/${details.user_id}/profile_photos/${details.photo}`} alt="" />
                        ) : (
                            <svg className="h-full w-full p-6 text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <path stroke="currentColor" strokeWidth="1.5" d="M7 17v1a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1a3 3 0 0 0-3-3h-4a3 3 0 0 0-3 3Zm8-9a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                            </svg>
                        )}
                        <button type="button" onClick={() => { setPhotoError(''); setPhotoModal(true); }}
                            className="absolute inset-x-0 bottom-0 bg-black/60 py-1.5 text-xs font-medium !text-white hover:bg-black/75">Alterar foto</button>
                    </div>
                    <div className="min-w-0 flex-1 space-y-2 text-center sm:text-left">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Podcast</p>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{name}</h1>
                        <p className="text-sm text-gray-700">{episodes.length} {episodes.length === 1 ? 'episódio' : 'episódios'}{details.first_name ? ` · ${[details.first_name, details.last_name].filter(Boolean).join(' ')}` : ''}</p>
                        {details.bio && <p className="text-sm text-gray-600 whitespace-pre-line">{details.bio}</p>}
                        {(details.email || details.phone) && <p className="break-all text-sm text-gray-500">{[details.email, details.phone].filter(Boolean).join(' · ')}</p>}
                        <div className="pt-2">
                            <Link to={`/podcasts?podcaster=${details.user_id || artist_id}`}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium !text-white hover:bg-amber-600">
                                <MdCloudUpload size={16} /> Carregar episódio
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Episodes */}
            <section className="rounded-xl border border-gray-200 bg-white">
                <div className="border-b px-4 py-3">
                    <h2 className="text-lg font-semibold text-gray-900">Episódios</h2>
                    <ul className="mt-1.5 space-y-0.5 text-xs [&_li]:!p-0 [&_li]:!text-gray-500">
                        <li className="flex items-start gap-1.5"><MdPlayArrow size={14} className="mt-px shrink-0" aria-hidden="true" /><span>Toca no ▶ para ouvir aqui mesmo.</span></li>
                        <li className="flex items-start gap-1.5"><MdArrowUpward size={14} className="mt-px shrink-0" aria-hidden="true" /><span>As setas mudam a ordem em que os episódios aparecem na app e no site. Episódios novos entram no topo.</span></li>
                        <li className="flex items-start gap-1.5"><MdEdit size={14} className="mt-px shrink-0" aria-hidden="true" /><span><strong className="font-medium text-gray-700">Editar</strong> muda o título e a categoria. O áudio e a capa não se mudam aqui; para isso, carrega o episódio de novo e apaga o antigo.</span></li>
                        <li className="flex items-start gap-1.5"><MdDelete size={14} className="mt-px shrink-0 text-rose-600" aria-hidden="true" /><span><strong className="font-medium text-gray-700">Apagar</strong> tira o episódio da app e do site para sempre.</span></li>
                    </ul>
                </div>
                {episodes.length > 0 ? (
                    <ul className="divide-y divide-gray-100 [&>li]:!p-0">
                        {episodes.map((ep, i) => {
                            const playing = playingId === ep.podcast_id;
                            return (
                                <li key={ep.podcast_id}>
                                    <div className={`flex items-center gap-2 px-3 py-3 sm:gap-3 sm:px-4 ${playing ? 'bg-amber-50' : 'hover:bg-gray-50'}`}>
                                        <div className="flex shrink-0 flex-col">
                                            <button type="button" onClick={() => moveEpisode(i, -1)} disabled={i === 0} aria-label="Subir"
                                                className="rounded p-0.5 text-gray-500 hover:bg-gray-100 disabled:text-gray-200"><MdArrowUpward size={14} /></button>
                                            <button type="button" onClick={() => moveEpisode(i, 1)} disabled={i === episodes.length - 1} aria-label="Descer"
                                                className="rounded p-0.5 text-gray-500 hover:bg-gray-100 disabled:text-gray-200"><MdArrowDownward size={14} /></button>
                                        </div>
                                        <button type="button" onClick={() => togglePlay(ep)} disabled={!ep.audio}
                                            aria-label={playing ? `Pausar ${ep.title}` : `Ouvir ${ep.title}`}
                                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full !text-white ${playing ? 'bg-amber-500 hover:bg-amber-600' : 'bg-gray-900 hover:bg-gray-700'} disabled:opacity-30`}>
                                            {playing ? <MdPause size={18} /> : <MdPlayArrow size={18} />}
                                        </button>
                                        {ep.cover
                                            ? <img src={ep.cover} alt="" loading="lazy" className="h-11 w-11 shrink-0 rounded object-cover" />
                                            : <div className="h-11 w-11 shrink-0 rounded bg-gray-200" />}
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm font-medium text-gray-900" title={ep.title}>{ep.title}</p>
                                            <p className="truncate text-xs text-gray-500">{[genreLabel(ep.genre), formatDuration(ep.duration)].filter(Boolean).join(' · ') || name}</p>
                                        </div>
                                        <button type="button" onClick={() => { setEditError(''); setEditing({ podcast_id: ep.podcast_id, title: ep.title || '', genre: ep.genre || '' }); }}
                                            aria-label={`Editar ${ep.title}`} title="Editar"
                                            className="shrink-0 rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-amber-600"><MdEdit size={18} /></button>
                                        <button type="button" onClick={() => deleteEpisode(ep)}
                                            aria-label={`Apagar ${ep.title}`} title="Apagar"
                                            className="shrink-0 rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"><MdDelete size={18} /></button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <div className="py-12 text-center">
                        <p className="text-sm text-gray-500">Este podcaster ainda não tem episódios.</p>
                        <Link to={`/podcasts?podcaster=${details.user_id || artist_id}`} className={`mt-3 inline-flex items-center gap-1.5 ${outlineBtn}`}>
                            <MdCloudUpload size={16} /> Carregar o primeiro
                        </Link>
                    </div>
                )}
            </section>

            {/* Edit episode modal */}
            {editing && (
                <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4" onClick={() => !savingEdit && setEditing(null)}>
                    <form onSubmit={saveEdit} onClick={(e) => e.stopPropagation()}
                        className="w-full space-y-4 rounded-t-2xl bg-white p-5 text-left shadow-xl sm:max-w-md sm:rounded-2xl">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">Editar episódio</h3>
                            <button type="button" onClick={() => setEditing(null)} aria-label="Fechar" className="rounded-lg p-1 text-2xl leading-none text-gray-400 hover:text-gray-700">×</button>
                        </div>
                        <label className="block">
                            <span className="mb-1 block text-sm font-medium text-gray-700">Título</span>
                            <input autoFocus maxLength={150} value={editing.title} onChange={(e) => setEditing(v => ({ ...v, title: e.target.value }))}
                                className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:ring-amber-500" />
                            {/\.(mp3|m4a|mpeg|wav)\b/i.test(editing.title) && (
                                <span className="mt-1 block text-xs text-amber-700">O título parece ter a extensão do ficheiro. Podes tirá-la.</span>
                            )}
                        </label>
                        <label className="block">
                            <span className="mb-1 block text-sm font-medium text-gray-700">Categoria</span>
                            <select value={editing.genre} onChange={(e) => setEditing(v => ({ ...v, genre: e.target.value }))}
                                className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:ring-amber-500">
                                <option value="">Sem categoria</option>
                                {editing.genre && !PODCAST_GENRES.some(g => g.value === editing.genre) && <option value={editing.genre}>{editing.genre}</option>}
                                {PODCAST_GENRES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                            </select>
                        </label>
                        {editError && <p className="text-sm text-red-600">{editError}</p>}
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <button type="button" onClick={() => setEditing(null)} disabled={savingEdit}
                                className="rounded-lg !border !border-solid border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancelar</button>
                            <button type="submit" disabled={savingEdit}
                                className="rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-medium !text-white hover:bg-amber-600 disabled:opacity-50">{savingEdit ? 'A guardar…' : 'Guardar'}</button>
                        </div>
                    </form>
                </div>
            )}

            {/* Photo modal */}
            {photoModal && (
                <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4" onClick={() => !uploadingPhoto && setPhotoModal(false)}>
                    <form onSubmit={uploadPhoto} onClick={(e) => e.stopPropagation()}
                        className="w-full space-y-4 rounded-t-2xl bg-white p-5 text-left shadow-xl sm:max-w-md sm:rounded-2xl">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">Actualizar foto</h3>
                            <button type="button" onClick={() => setPhotoModal(false)} aria-label="Fechar" className="rounded-lg p-1 text-2xl leading-none text-gray-400 hover:text-gray-700">×</button>
                        </div>
                        {photo && <img src={photo.url} alt="" className="mx-auto h-40 w-40 rounded-xl border object-cover" />}
                        <div>
                            <input onChange={pickPhoto} accept="image/png, image/jpeg" type="file" aria-describedby="podcaster_photo_help"
                                className="block w-full cursor-pointer rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 focus:outline-none" />
                            <ul id="podcaster_photo_help" className="mt-2 space-y-0.5 text-xs [&_li]:!p-0 [&_li]:!text-gray-500">
                                <li><strong className="font-medium text-gray-700">Formato:</strong> JPG ou PNG</li>
                                <li><strong className="font-medium text-gray-700">Tamanho ideal:</strong> quadrada, 1200 × 1200 px ou mais</li>
                                <li><strong className="font-medium text-gray-700">Peso máximo:</strong> {PHOTO_MAX_MB} MB</li>
                            </ul>
                            {photoError && <p className="mt-2 text-sm text-red-600">{photoError}</p>}
                        </div>
                        <button type="submit" disabled={!photo || uploadingPhoto}
                            className="w-full rounded-lg bg-amber-500 px-5 py-2.5 text-sm font-medium !text-white hover:bg-amber-600 disabled:opacity-50">
                            {uploadingPhoto ? 'A enviar…' : 'Actualizar foto'}
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
}
